import { Button, Icon, Size, Theme, Tray } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FileUploader, Thumbnail, ThumbnailTray } from '../../..';
import { DeleteIcon } from '../../../../assets/icons';
import { IFileWithPreview, IMediaEntryUploaderValue } from '../../../../types/FileUploader';
import { IMediaEntry } from '../../../../types/MediaEntry';

interface IMediaEntryUploaderProps {
  className?: string | object;
  onChange: (value: IMediaEntryUploaderValue) => void;
  mediaEntries: IMediaEntry[]; // Present already existing media entries in the header
}

function MediaEntryUploader(props: IMediaEntryUploaderProps) {
  const { mediaEntries, className, onChange, ...other } = props;

  const classes = classNames(
    {
      'media-entry-uploader': true,
    },
    className
  );

  const [filesWithPreview, setFilesWithPreview] = useState<IFileWithPreview[]>([]);
  const [mediaEntriesToDelete, setMediaEntriesToDelete] = useState<IMediaEntry[]>([]);

  useEffect(
    () =>
      onChange({
        mediaEntriesToDelete,
        newFiles: filesWithPreview.map((fileWithPreview) => fileWithPreview.file),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filesWithPreview, mediaEntriesToDelete]
  );

  // Get all passed media entries that the user has not selected for deletion
  function getMediaEntriesToDisplay() {
    return mediaEntries.filter((mediaEntry: IMediaEntry) => {
      return !mediaEntriesToDelete.some((deleted) => deleted.id === mediaEntry.id);
    });
  }

  // Render thumbnails for existing media entries and uploaded files
  function renderThumbnails() {
    const mediaEntriesToDisplay = getMediaEntriesToDisplay();

    if (filesWithPreview.length || mediaEntriesToDisplay.length) {
      return (
        <ThumbnailTray>
          {mediaEntriesToDisplay.map((mediaEntry: IMediaEntry) => {
            return renderMediaEntryThumbnail(mediaEntry);
          })}
          {filesWithPreview.map((file: IFileWithPreview) => {
            return renderFileThumbnail(file);
          })}
        </ThumbnailTray>
      );
    } else {
      return null;
    }
  }

  function handleFileLoaderChange(newFilesWithPreview: IFileWithPreview[]) {
    setFilesWithPreview(newFilesWithPreview);
  }

  function handleDeleteMediaEntry(mediaEntry: IMediaEntry) {
    setMediaEntriesToDelete([...mediaEntriesToDelete, mediaEntry]);
  }

  function handleDeleteFile(fileWithPreview: IFileWithPreview) {
    setFilesWithPreview(
      filesWithPreview.filter((file) => {
        return file.preview !== fileWithPreview.preview;
      })
    );
  }

  function renderMediaEntryThumbnail(mediaEntry: IMediaEntry) {
    const button = deleteButton(() => handleDeleteMediaEntry(mediaEntry));

    return <Thumbnail key={mediaEntry.id} fileUrl={mediaEntry.fileUrl} dockTemplate={button} />;
  }

  function renderFileThumbnail(fileWithPreview: IFileWithPreview) {
    const button = deleteButton(() => handleDeleteFile(fileWithPreview));

    return <Thumbnail key={fileWithPreview.preview} fileUrl={fileWithPreview.preview} dockTemplate={button} />;
  }

  function deleteButton(onClick: () => void) {
    return (
      <Tray className="p-1">
        <Button size={Size.SMALL} type="icon" theme={Theme.DARK} onClick={onClick}>
          <Icon svg={DeleteIcon} />
        </Button>
      </Tray>
    );
  }

  return (
    <>
      <FileUploader
        filesWithPreview={filesWithPreview}
        header={renderThumbnails()}
        onChange={handleFileLoaderChange}
        className={classes}
        {...other}
      />
    </>
  );
}

MediaEntryUploader.defaultProps = {
  mediaEntries: [],
};

export default MediaEntryUploader;
